import { combineActions, handleActions } from 'redux-actions';
import produce from 'immer';
import update from 'immutability-helper';
import Cookies from 'universal-cookie';

import * as types from '@src/store/types';
import { reactLocalStorage } from '@src/utils/localStorage';
import { sentrySetContext } from '@src/utils/sentry';
import { startLogRocket } from '@src/utils/logRocket';
import config from '@src/config';

const { useSentry } = config;
const initialState = {
	isSignedIn: false,
	token: null,
	isPreview: false,
	previewToken: null,
	buttonLoading: false,
	successMessage: '',
	updateMobileState: 'IDLE',
	confirmMobileState: 'IDLE',
	emailResendState: 'IDLE',
	isRecordIssue: false,
};
const cookies = new Cookies();

export default handleActions(
	{
		[types.CHECK_IS_SIGNED_IN](state) {
			const isJwtToken = reactLocalStorage.get('isJwtToken');
			const jwtEnabled = isJwtToken === true || isJwtToken === 'true';

			return produce(state, draft => {
				draft.isSignedIn = reactLocalStorage.get('isSignedIn');
				// JWT 的 access_token 只存 Redux memory，不會寫進 localStorage。
				// 這裡若仍從 localStorage 覆寫會把剛 boot refresh 拿到的 token 洗掉，導致下一個 getProfile 先 401 再觸發 interceptor 重新 refresh。
				if (!jwtEnabled) {
					draft.token = reactLocalStorage.get('token');
				}
			});
		},
		[types.SET_TOKEN](state, { payload: { token, isJwtToken } }) {
			const jwtEnabled = isJwtToken === true;

			reactLocalStorage.set('isSignedIn', true);
			reactLocalStorage.set('isJwtToken', jwtEnabled);
			reactLocalStorage.remove('previewToken');

			// JWT access_token 只放 Redux memory，降低 XSS 透過 localStorage 竊取的風險。
			// Session token（非 JWT 登入方式，例如 OTP/LINE）仍寫入 localStorage / cookie 維持現狀。
			if (jwtEnabled) {
				reactLocalStorage.remove('token');
				cookies.remove('token', { path: '/' });
			} else {
				reactLocalStorage.set('token', token);
				cookies.set('token', token, { path: '/' });
			}

			if (useSentry && token) {
				sentrySetContext('header', {
					token: `${token.slice(0, 4)}...${token.slice(-8)}`,
				});
			}

			return produce(state, draft => {
				draft.isSignedIn = true;
				draft.token = token;
			});
		},
		[types.SET_PREVIEW_TOKEN](state, { payload: { token } }) {
			reactLocalStorage.set('previewToken', token);
			reactLocalStorage.set('isSignedIn', false);
			reactLocalStorage.remove('token');
			cookies.remove('token', { path: '/' });

			return produce(state, draft => {
				draft.isPreview = true;
				draft.previewToken = token;
				draft.isSignedIn = false;
				draft.token = '';
			});
		},
		[combineActions(
			types.EMAIL_LOGIN_CALL,
			types.EMAIL_REGISTER_CALL,
			types.EMAIL_RE_SEND_CALL,
			types.CONFIRM_MOBILE_CALL,
			types.UPDATE_MOBILE_CALL,
			types.CHECK_MOBILE_LINE_BOUND_CALL,
		)](state, { payload }) {
			return produce(state, draft => {
				draft.buttonLoading = true;
				draft.successMessage = '';
			});
		},
		[combineActions(
			types.EMAIL_LOGIN_DONE,
			types.EMAIL_LOGIN_FAIL,
			types.EMAIL_REGISTER_DONE,
			types.EMAIL_REGISTER_FAIL,
			types.EMAIL_RE_SEND_DONE,
			types.EMAIL_RE_SEND_FAIL,
			types.CONFIRM_MOBILE_DONE,
			types.CONFIRM_MOBILE_FAIL,
			types.UPDATE_MOBILE_DONE,
			types.CHECK_MOBILE_LINE_BOUND_DONE,
			types.CHECK_MOBILE_LINE_BOUND_FAIL,
		)](state, { payload }) {
			return produce(state, draft => {
				draft.buttonLoading = false;
			});
		},
		[types.EMAIL_RE_SEND_DONE](state, { payload }) {
			return produce(state, draft => {
				draft.successMessage = 'share:auth:check_email:success';
			});
		},
		[types.UPDATE_MOBILE_CALL](state, { payload }) {
			return produce(state, draft => {
				draft.updateMobileState = 'LOADING';
			});
		},
		[types.UPDATE_MOBILE_DONE](state, { payload }) {
			return produce(state, draft => {
				draft.updateMobileState = 'SUCCESS';
			});
		},
		[types.UPDATE_MOBILE_FAIL](state, { payload }) {
			return produce(state, draft => {
				draft.updateMobileState = 'FAIL';
			});
		},
		[types.CONFIRM_MOBILE_CALL](state, { payload }) {
			return produce(state, draft => {
				draft.confirmMobileState = 'LOADING';
			});
		},
		[types.CONFIRM_MOBILE_DONE](state, { payload }) {
			return produce(state, draft => {
				draft.confirmMobileState = 'SUCCESS';
			});
		},
		[types.CONFIRM_MOBILE_FAIL](state, { payload }) {
			return produce(state, draft => {
				draft.confirmMobileState = 'FAIL';
			});
		},
		[types.RESET_UPDATE_MOBILE](state) {
			return produce(state, draft => {
				draft.updateMobileState = 'IDLE';
				draft.confirmMobileState = 'IDLE';
			});
		},
		[types.LOGOUT_CALL](state, { payload }) {
			return produce(state, draft => {
				draft.isSignedIn = false;
				draft.buttonLoading = false;
				draft.successMessage = '';
			});
		},
		[types.LOGOUT_DONE](state, { payload }) {
			reactLocalStorage.remove('token');
			reactLocalStorage.remove('isSignedIn');
			reactLocalStorage.remove('isJwtToken');
			reactLocalStorage.remove('schoolId');
			cookies.remove('token', { path: '/' });

			return {
				isSignedIn: false,
				token: null,
				buttonLoading: false,
				successMessage: '',
			};
		},
		[types.EMAIL_RE_SEND_CALL](state, { payload }) {
			return produce(state, draft => {
				draft.emailResendState = 'LOADING';
			});
		},
		[types.EMAIL_RE_SEND_DONE](state, { payload }) {
			return produce(state, draft => {
				draft.emailResendState = 'SUCCESS';
			});
		},
		[types.EMAIL_RE_SEND_FAIL](state, { payload }) {
			return produce(state, draft => {
				const { message } = payload.response.data;

				if (message === 'Conflict') {
					draft.emailResendState = 'FAIL_EMAIL_EXISTED';
				} else {
					draft.emailResendState = 'FAIL';
				}
			});
		},
		[types.EMAIL_RESEND_STATE_RESET](state) {
			return produce(state, draft => {
				draft.emailResendState = 'IDLE';
			});
		},
		[types.RECORD_ISSUE_START](state, { payload }) {
			startLogRocket(payload.userId, payload);
			return produce(state, draft => {
				draft.isRecordIssue = true;
			});
		},
		[types.RECORD_ISSUE_STOP](state) {
			return produce(state, draft => {
				draft.isRecordIssue = false;
			});
		},
		[combineActions(
			types.CHECK_LOGIN_FAIL,
			types.CHECK_LOGIN_SESSION_FAIL,
			types.OAUTH_EXCHANGE_FAIL,
		)](state) {
			reactLocalStorage.set('isSignedIn', false);
			return update(state, {
				isSignedIn: { $set: false },
			});
		},
	},
	initialState,
);
